import { createDateFormatter } from "@nexus/ui";
import {
  categoryNoDataAreas,
  CHART_LEGEND_ICONS,
  escapeHtml,
  NO_DATA_LABEL,
  positionChartTooltip,
} from "@nexus/widget-kit";
import { withAlpha } from "@nexus/widget-sdk";
import type { ChartColors } from "@nexus/widget-sdk";
import type { BarSeriesOption } from "echarts/charts";
import type {
  GridComponentOption,
  LegendComponentOption,
  MarkAreaComponentOption,
  TooltipComponentOption,
} from "echarts/components";
import type * as echarts from "echarts/core";
import { groupColors } from "./colors";
import type { WeeklyModel } from "./model";

export type WeeklyOption = echarts.ComposeOption<
  | BarSeriesOption
  | GridComponentOption
  | LegendComponentOption
  | MarkAreaComponentOption
  | TooltipComponentOption
>;

export interface ChartTexts {
  total: string;
  unfinished: string;
  lowSample: string;
  lowSampleHint: string;
}

const fontBase = { fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" };
const countFormat = new Intl.NumberFormat("en");
const dayFormat = createDateFormatter(
  "utc",
  {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  },
  "en",
);

export const formatCount = (value: number) => countFormat.format(value);
export const formatShare = (value: number) =>
  `${value.toFixed(value >= 10 || value === 0 ? 0 : 1)}%`;

/** Rough advance of one Inter character, in ems: enough to tell when labels would collide. */
const CHAR_EM = 0.6;

/**
 * Which week labels fit under the bars, from the room one week has. A narrow
 * chart (three or more per row, a 720p wall) keeps the week names readable
 * instead of overprinting them: it drops the detection counts first (they stay
 * in the tooltip), then the "unfinished" note (the dashed bar still says it),
 * and only then shows every other week name — even when every week label was
 * asked for, since overprinted names are never readable.
 */
export function weekLabelPlan(
  model: Pick<WeeklyModel, "weeks">,
  texts: Pick<ChartTexts, "unfinished">,
  labelSize: number,
  slot: number,
  allWeekLabels: boolean,
) {
  const width = (text: string, size: number) => text.length * size * CHAR_EM;
  const longestWeek = Math.max(0, ...model.weeks.map((week) => week.label.length));
  const longestCount = Math.max(
    0,
    ...model.weeks.map((week) => `n=${formatCount(week.total)}`.length),
  );
  const weekWidth = longestWeek * labelSize * CHAR_EM + 4;
  // "All" labels every week while the names fit side by side; automatic labels keep a margin.
  const needed = allWeekLabels ? weekWidth : weekWidth + 8;
  const step = slot >= needed ? 1 : Math.max(1, Math.ceil(needed / slot));
  const room = slot * step;
  return {
    step,
    counts: room >= longestCount * (labelSize - 1) * CHAR_EM + 4,
    unfinished: room >= width(texts.unfinished, labelSize - 1) + 4,
  };
}

export function buildOption(
  model: WeeklyModel,
  colors: ChartColors,
  texts: ChartTexts,
  options?: {
    allWeekLabels?: boolean;
    /** The chart's current width, read at each layout so labels follow a resize. */
    chartWidth?: () => number;
    /** Draws the chart in (see `useChartAnimation`); on unless told otherwise. */
    animate?: boolean;
  },
): WeeklyOption {
  const labelSize = colors.typography?.labelSize ?? 12;
  // Room for one week: the plot is the chart minus the percentage axis and the grid margins.
  const plan = () => {
    const width = options?.chartWidth?.() ?? 0;
    const slot =
      width > 0 ? (width - labelSize * 3.2 - 24) / Math.max(1, model.weeks.length) : Infinity;
    return weekLabelPlan(model, texts, labelSize, slot, options?.allWeekLabels ?? false);
  };
  const palette = groupColors(model.groups, model.families);
  const hatch = {
    symbol: "rect",
    symbolSize: 1,
    dashArrayX: [1, 0],
    dashArrayY: [2, 4],
    rotation: -Math.PI / 4,
    color: withAlpha(colors.tooltipBg, 0.7),
  };
  return {
    animation: options?.animate ?? true,
    animationDuration: 400,
    animationEasing: "cubicOut",
    backgroundColor: "transparent",
    grid: { top: 16, right: 16, bottom: 36, left: 8, containLabel: true },
    legend: {
      bottom: 0,
      left: 0,
      type: "scroll",
      data: model.groups,
      icon: CHART_LEGEND_ICONS.rect,
      itemWidth: 12,
      itemHeight: 8,
      itemGap: 16,
      textStyle: { color: colors.label, fontSize: labelSize, ...fontBase },
    },
    tooltip: {
      trigger: "axis",
      appendTo: () => document.body,
      transitionDuration: 0,
      position: (point, _params, _dom, _rect, size) => positionChartTooltip(point, size),
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      borderRadius: 8,
      padding: [10, 12],
      textStyle: { color: colors.tooltipText, fontSize: labelSize, ...fontBase },
      axisPointer: { type: "shadow", shadowStyle: { color: withAlpha(colors.label, 0.08) } },
      formatter: (params: unknown) => {
        const items = (Array.isArray(params) ? params : [params]) as Array<{
          seriesIndex?: number;
          marker?: string;
          dataIndex?: number;
        }>;
        const weekIndex = items[0]?.dataIndex;
        if (typeof weekIndex !== "number") return "";
        const week = model.weeks[weekIndex];
        const date = dayFormat.format(new Date(`${week.weekStart}T00:00:00Z`));
        const heading = `<div style="margin-bottom:4px;opacity:0.75">${escapeHtml(week.label)} · ${escapeHtml(date)}</div>`;
        if (week.missing) return `${heading}<div>${NO_DATA_LABEL}</div>`;
        const notes = [
          week.partial ? `<div>${escapeHtml(texts.unfinished)}</div>` : "",
          week.lowSample
            ? `<div>${escapeHtml(texts.lowSample)} (${formatCount(week.total)}): ${escapeHtml(texts.lowSampleHint)}</div>`
            : "",
        ].join("");
        const rows = items
          .map((item) => item.seriesIndex ?? -1)
          .filter((group) => group >= 0 && model.counts[group][weekIndex] > 0)
          .sort((a, b) => model.counts[b][weekIndex] - model.counts[a][weekIndex])
          .map(
            (group) =>
              `<div style="display:flex;justify-content:space-between;gap:16px"><span>${items.find((item) => item.seriesIndex === group)?.marker ?? ""}${escapeHtml(model.groups[group])}</span><b>${formatShare(model.shares[group][weekIndex])} <span style="opacity:0.7;font-weight:400">(${formatCount(model.counts[group][weekIndex])})</span></b></div>`,
          )
          .join("");
        return `${heading}${notes ? `<div style="margin-bottom:6px;color:${colors.label}">${notes}</div>` : ""}${rows}<div style="display:flex;justify-content:space-between;gap:16px;margin-top:6px;padding-top:6px;border-top:1px solid ${withAlpha(colors.label, 0.25)}"><span>${escapeHtml(texts.total)}</span><b>${formatCount(week.total)}</b></div>`;
      },
    },
    xAxis: {
      type: "category",
      data: model.weeks.map((week) => week.weekStart),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: colors.axisLine } },
      axisLabel: {
        color: colors.label,
        fontSize: labelSize,
        margin: 10,
        ...(options?.allWeekLabels || options?.chartWidth
          ? { interval: (index: number) => index % plan().step === 0 }
          : {}),
        lineHeight: labelSize + 4,
        ...fontBase,
        formatter: (_value: string, index: number) => {
          const week = model.weeks[index];
          if (week.missing) return `{week|${week.label}}`;
          const { counts, unfinished } = plan();
          const lines = [`{week|${week.label}}`];
          if (counts) lines.push(`{count|n=${formatCount(week.total)}}`);
          if (week.partial && unfinished) lines.push(`{flag|${texts.unfinished}}`);
          return lines.join("\n");
        },
        rich: {
          week: { color: colors.label, fontSize: labelSize, ...fontBase },
          count: { color: withAlpha(colors.label, 0.6), fontSize: labelSize - 1, ...fontBase },
          flag: {
            color: withAlpha(colors.label, 0.6),
            fontSize: labelSize - 1,
            fontStyle: "italic",
            ...fontBase,
          },
        },
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: 100,
      interval: 25,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: colors.grid } },
      axisLabel: { color: colors.label, fontSize: labelSize, formatter: "{value}%", ...fontBase },
    },
    series: model.groups.map((group, position) => {
      const color = palette.get(group)?.chart(colors) ?? colors.series[0];
      return {
        name: group,
        type: "bar",
        stack: "share",
        barMaxWidth: 56,
        barCategoryGap: "26%",
        emphasis: { focus: "series" },
        itemStyle: { color, borderColor: colors.tooltipBg, borderWidth: 1 },
        ...(position === 0
          ? {
              markArea: {
                silent: true,
                data: categoryNoDataAreas(
                  model.weeks.map((week) => week.weekStart),
                  model.weeks.map((week) => week.missing),
                  colors,
                ),
              },
            }
          : {}),
        data: model.shares[position].map((share, weekIndex) => {
          const week = model.weeks[weekIndex];
          if (!week.lowSample && !week.partial) return share;
          return {
            value: share,
            itemStyle: {
              color: withAlpha(color, week.lowSample ? 0.35 : 0.6),
              ...(week.lowSample ? { decal: hatch } : {}),
              ...(week.partial ? { borderType: "dashed" as const, borderColor: color } : {}),
            },
          };
        }),
      } satisfies BarSeriesOption;
    }),
  };
}
